/* eslint-disable promise/prefer-await-to-then */
import {
	ActionButton,
	Checkbox,
	DetailsList,
	getFocusStyle,
	getTheme,
	HoverCard,
	HoverCardType,
	IColumn,
	Icon,
	IconButton,
	ITheme,
	Link,
	mergeStyleSets,
	MessageBar,
	MessageBarType,
	ProgressIndicator,
	ScrollablePane,
	ScrollbarVisibility,
	SelectionMode,
	Stack,
	StackItem,
	Sticky,
	StickyPositionType
} from "@fluentui/react"
import { FileIconType, getFileTypeIconProps, initializeFileTypeIcons } from "@fluentui/react-file-type-icons"
import saveAs from "file-saver"
import { memo, useEffect, useRef, useState } from "react"
import { getCurrentUserContactId, getCurrentUserName } from "../../hooks/generalHooks"
import { getErrorText, safeFormatDateValue } from "../../lib/helper"
import sharePointService, { DocumentFolderType, IDocumentOrFolder } from "../../services/sharePointService"
import { DragDropFile } from "../drag-drop-file"

initializeFileTypeIcons()
const theme: ITheme = getTheme()
const { palette, semanticColors, fonts } = theme
const itemCellBase = {
	padding: 10,
	boxSizing: "border-box",
	display: "flex",
	fontSize: "22px"
}
const classNames = mergeStyleSets({
	itemCell: [
		getFocusStyle(theme, { inset: -1 }),
		{
			...itemCellBase,
			selectors: {
				"&:hover": { background: palette.neutralLight }
			},
			borderBottom: `1px solid ${semanticColors.bodyDivider}`,
			cursor: "pointer"
		}
	],
	itemCellNoHover: itemCellBase,
	itemContent: {
		marginLeft: 10,
		overflow: "hidden",
		flexGrow: 1
	},
	itemName: [
		fonts.medium,
		{
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis"
		}
	],
	fieldLabel: {
		fontSize: "18px",
		marginBottom: "8px",
		selectors: {
			"&>.sub": {
				fontSize: "13px",
				opacity: ".7"
			}
		}
	},
	fileIconCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: "16px",
		padding: "4px !important"
	}
})

export enum SelectionType {
	onlyFiles,
	onlyFolders,
	all
}

export interface FolderFilePickerProps {
	serverRelativeUrl: string
}

export interface EntityFilePickerProps {
	entityId: string
}

interface IFolderFilePickerProps {
	brokerFilePickerProps?: EntityFilePickerProps
	selectionType: SelectionType
	allowMultipleSelection: boolean
	selectedDocuments: IDocumentOrFolder[]
	onSelectionMade: (selection: IDocumentOrFolder[]) => void
	disabled: boolean
	allowFileDownload: boolean
	allowSelection: boolean
	showBorder?: boolean
	startBelowNodePath?: string
	minHeight?: number
	shouldShowLinkToSharePointButton?: boolean
}

function FolderFilePickerInternal(props: IFolderFilePickerProps) {
	const {
		brokerFilePickerProps,
		selectionType,
		allowMultipleSelection,
		selectedDocuments,
		onSelectionMade,
		disabled,
		allowFileDownload,
		allowSelection,
		showBorder,
		startBelowNodePath,
		minHeight,
		shouldShowLinkToSharePointButton
	} = props

	const currentUserContactId = getCurrentUserContactId()
	const currentUserName = getCurrentUserName()

	const [selectedOption, setSelectedOption] = useState<string>()
	const [loading, setLoading] = useState(true)
	const [uploading, setUploading] = useState(false)
	const [error, setError] = useState<string | undefined>()
	const [selectedDocs, setSelectedDocs] = useState<IDocumentOrFolder[]>(selectedDocuments)
	const [options, setOptions] = useState<IDocumentOrFolder[]>([])

	const _startBelowNodePath = useRef<string | undefined>(startBelowNodePath)
	const _currentSetOfDocuments = useRef<IDocumentOrFolder[]>([])
	const _rootPath = useRef("")

	const _setNewFolder = async (url: string | undefined) => {
		if (url) {
			setLoading(true)
			const folders = await sharePointService.getAccountDocumentsForBroker(currentUserContactId, currentUserName, brokerFilePickerProps.entityId, url)
			setSelectedOption(url)
			setLoading(false)
			setOptions([...(folders || [])].sort((a, b) => (a.FolderOrFile === b.FolderOrFile ? (a.FileLeafRef > b.FileLeafRef ? 1 : -1) : a.FolderOrFile === DocumentFolderType.Folder ? -1 : 1)))
		}
	}

	const _getParentUrl = (url: string) => {
		const parentUrl = url === "/" ? undefined : url?.substring(1, url.lastIndexOf("/"))
		return parentUrl
	}

	const _getBreadCrumbTitle = (url: string) => {
		if (!url) {
			return ""
		}
		const parentUrl = url.substring(1).replace(/\//g, " / ").replace(/"/g, "")
		if (!parentUrl) {
			return "Portal Documents"
		} else {
			return `Portal Documents / ${parentUrl}`
		}
	}

	const _renderBreadcrumb = (): JSX.Element => {
		let retVal = <span>Portal Documents</span>

		if (selectedOption && !loading) {
			const folder = selectedOption
			if (folder) {
				const parentUrl = _getParentUrl(selectedOption)
				retVal = (
					<Stack horizontal title={_getBreadCrumbTitle(selectedOption)}>
						{parentUrl && (
							<StackItem>
								<IconButton
									onClick={ev => {
										!disabled && _setNewFolder(parentUrl)
									}}
									iconProps={{ iconName: parentUrl ? "Up" : "Back" }}
								/>
							</StackItem>
						)}
						<StackItem styles={{ root: { marginTop: 5 } }}>
							<span>{_getBreadCrumbTitle(selectedOption)}</span>
						</StackItem>
					</Stack>
				)
			}
		}
		return retVal
	}

	const _onRenderDestinationListCell = (option?: IDocumentOrFolder) => {
		const divProps: any = { className: classNames.itemCellNoHover }
		if (option!.FolderOrFile === DocumentFolderType.Folder) {
			divProps["data-is-focusable"] = true
			divProps.onClick = () => !disabled && _setNewFolder(option!.ServerRelativeUrl)
		}
		const fileType = option?.FolderOrFile === DocumentFolderType.File ? option?.ServerRelativeUrl.substring(option.ServerRelativeUrl.lastIndexOf(".") + 1) : undefined
		const filePath = option!.FolderRelativeUrl
		const title =
			allowFileDownload && option?.FolderOrFile === DocumentFolderType.File ? (
				<Link
					onClick={() => {
						sharePointService
							.downloadPortalDocument(currentUserContactId, currentUserName, brokerFilePickerProps.entityId, filePath)
							.then(res => {
								saveAs(res, option?.FileLeafRef)
							})
							.catch(err => {
								setError(getErrorText(err))
							})
					}}>
					{option?.FileLeafRef}
				</Link>
			) : (
				option?.FileLeafRef
			)
		let isCheckboxDisabled = true
		switch (selectionType) {
			case SelectionType.all:
				isCheckboxDisabled = false
				break
			case SelectionType.onlyFiles:
				isCheckboxDisabled = option?.FolderOrFile === DocumentFolderType.Folder
				break
			case SelectionType.onlyFolders:
				isCheckboxDisabled = option?.FolderOrFile === DocumentFolderType.File
				break
		}
		return (
			<div>
				<div className={classNames.itemContent}>
					<Stack horizontal className={classNames.itemName}>
						{allowSelection && (
							<Checkbox
								disabled={disabled || isCheckboxDisabled}
								styles={{ root: { paddingTop: 10, paddingRight: 8 } }}
								checked={selectedDocs.map(d => d.ServerRelativeUrl).includes(option!.ServerRelativeUrl)}
								onChange={(ev, checked) => {
									let currentSelection = [...selectedDocs]
									if (allowMultipleSelection) {
										if (!currentSelection.includes(option!) && checked) {
											currentSelection.push(option!)
											setSelectedDocs(currentSelection)
										} else if (currentSelection.map(m => m.ServerRelativeUrl).includes(option!.ServerRelativeUrl) && !checked) {
											currentSelection = currentSelection.filter(d => d.ServerRelativeUrl !== option!.ServerRelativeUrl)
											setSelectedDocs(currentSelection)
										}
									} else {
										currentSelection = [option!]
										setSelectedDocs([option!])
									}
									onSelectionMade([...currentSelection])
								}}
							/>
						)}
						{option?.FolderOrFile === DocumentFolderType.Folder && (
							<Icon {...getFileTypeIconProps({ type: FileIconType.folder, size: 32 })} styles={{ root: { overflow: "visible", paddingTzop: 8 } }} />
						)}
						{option?.FolderOrFile === DocumentFolderType.File && (
							<Icon {...getFileTypeIconProps({ extension: fileType, size: 32 })} styles={{ root: { overflow: "visible", paddingTop: 8 } }} />
						)}
						<span {...divProps} style={{ whiteSpace: "normal", fontSize: 14, cursor: option?.FolderOrFile === DocumentFolderType.Folder ? "pointer" : "initial" }}>
							{title}
						</span>
					</Stack>
				</div>
			</div>
		)
	}

	const _loadTopLevelFolderOptions = (callBack?: () => void) => {
		setError(undefined)
		setLoading(true)
		const promise = sharePointService.getAccountDocumentsForBroker(currentUserContactId, currentUserName, brokerFilePickerProps.entityId)

		if (promise) {
			promise
				.then(async (documentsAndFolders: IDocumentOrFolder[]) => {
					_currentSetOfDocuments.current = documentsAndFolders
					if (!_startBelowNodePath.current && brokerFilePickerProps && documentsAndFolders.length > 0) {
						_startBelowNodePath.current = documentsAndFolders[0].ServerRelativeUrl.substring(0, documentsAndFolders[0].ServerRelativeUrl.lastIndexOf("/"))
					}
					let folders =
						_startBelowNodePath.current &&
						!_startBelowNodePath.current.toLocaleLowerCase().endsWith("portal documents") &&
						(await sharePointService.getAccountDocumentsForBroker(currentUserContactId, currentUserName, brokerFilePickerProps.entityId, _startBelowNodePath.current))
					_rootPath.current = options.length > 0 ? options[0].ServerRelativeUrl : undefined
					if (folders) {
						folders = folders.sort((a, b) => (a.FolderOrFile === b.FolderOrFile ? (a.FileLeafRef > b.FileLeafRef ? 1 : -1) : a.FolderOrFile === DocumentFolderType.Folder ? -1 : 1))
						setOptions([...folders])
						setLoading(false)
					} else {
						setLoading(false)
						let options = [...documentsAndFolders]
						options = options.sort((a, b) => (a.FolderOrFile === b.FolderOrFile ? (a.FileLeafRef > b.FileLeafRef ? 1 : -1) : a.FolderOrFile === DocumentFolderType.Folder ? -1 : 1))
						if (options.length > 0 && brokerFilePickerProps) {
							//options = options.slice(0, 1)
							_rootPath.current = options[0].FolderRelativeUrl
						}
						setOptions(options)
					}
					if (callBack) {
						callBack()
					}
				})
				.catch(err => {
					setLoading(false)
					setError(getErrorText(err))
				})
		} else {
			setLoading(false)
			setError("Incorrect parameters sent to the picker control Must send either 'folderFilePickerProps' or 'institutionFilePickerProps' or 'annualReportFilePickerProps'")
		}
	}

	useEffect(_loadTopLevelFolderOptions, [brokerFilePickerProps, currentUserContactId, currentUserName])

	const destListHeight = Math.max(options.length * 42, 300)

	const _onRenderPlainCardDetailsHeader = (props: any, renderer: any) => {
		return <Sticky stickyPosition={StickyPositionType.Header}>{renderer(props)}</Sticky>
	}

	const _onRenderPlainCard = (): JSX.Element => {
		const items = selectedDocs
		const itemListHeight = 44 + items.length * 44
		return (
			<div style={{ position: "relative", width: 500, height: Math.min(itemListHeight, minHeight ? minHeight + 8 : 308), border: "1px solid rgba(198, 198, 198, .6)" }}>
				<ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto} styles={{ root: { selectors: { "& .ms-DetailsHeader": { paddingTop: 0 } } } }}>
					<DetailsList
						onRenderDetailsHeader={_onRenderPlainCardDetailsHeader}
						columns={[
							{
								key: "type",
								name: "Type",
								fieldName: "type",
								iconName: "Page",
								isIconOnly: true,
								minWidth: 16,
								maxWidth: 16,
								className: classNames.fileIconCell,
								onRender: (item: IDocumentOrFolder) => {
									let fieldValue = null
									switch (item.FolderOrFile) {
										case DocumentFolderType.Folder: {
											fieldValue = <Icon iconName="FabricFolderFill" styles={{ root: { display: "contents" } }} />
											break
										}
										case DocumentFolderType.File: {
											const fileType = item.FileLeafRef?.split(".").pop()
											fieldValue = <Icon {...getFileTypeIconProps({ extension: fileType, size: 16 })} styles={{ root: { display: "contents" } }} />
											break
										}
									}
									return fieldValue
								}
							},
							{
								key: "Name",
								name: "Name",
								fieldName: "FileLeafRef",
								minWidth: 50,
								onRender: (doc: IDocumentOrFolder) => {
									const filePath = _getBreadCrumbTitle(doc.ServerRelativeUrl)
									return <span title={filePath}>{filePath}</span>
								}
							}
						]}
						items={items}
						selectionMode={SelectionMode.none}
					/>
				</ScrollablePane>
			</div>
		)
	}

	const _getColumns = (): IColumn[] => {
		return [
			{
				key: "Name",
				name: "Name",
				minWidth: 250,
				onRender: (item: IDocumentOrFolder) => {
					return _onRenderDestinationListCell(item)
				}
			},
			{
				key: "Modified By",
				name: "Modified By",
				minWidth: 160,
				onRender: (item: IDocumentOrFolder) => {
					return item.PortalModifiedBy
				}
			},
			{
				key: "Modified",
				name: "Modified",
				minWidth: 140,
				onRender: (item: IDocumentOrFolder) => {
					const formattedDate = safeFormatDateValue(item.ModifiedOn, "ff")
					return <span>{formattedDate}</span>
				}
			}
		]
	}

	return (
		<>
			<Stack horizontal>
				<Stack.Item grow>{_renderBreadcrumb()}</Stack.Item>
				<Stack.Item align="end">
					{!loading && selectedOption && shouldShowLinkToSharePointButton && (
						<IconButton title="Link to folder in SharePoint" iconProps={{ iconName: "SharepointLogo" }} href={selectedOption} target="_blank" />
					)}
				</Stack.Item>
				<Stack.Item align="end">
					{!loading && (
						<Stack horizontal tokens={{ childrenGap: 8 }}>
							<IconButton
								iconProps={{ iconName: "NewFolder" }}
								disabled={false}
								onClick={() => {
									//Create folder
								}}
							/>
							<IconButton
								iconProps={{ iconName: "Refresh" }}
								disabled={false}
								onClick={() => {
									_loadTopLevelFolderOptions(() => {
										_setNewFolder(selectedOption!)
									})
								}}
							/>
						</Stack>
					)}
				</Stack.Item>
			</Stack>
			{loading && <ProgressIndicator />}
			{!loading && (
				<DragDropFile
					multiple
					acceptedFiles={[
						"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
						"application/vnd.ms-excel",
						"application/pdf",
						"application/msword",
						"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
						"image/png",
						"image/jpeg"
					]}
					onFilesDropped={files => {
						if (files.length === 0) {
							setError("None of the files dropped are allowed.")
							return
						}
						setUploading(true)
						setError(undefined)
						sharePointService
							.uploadPortalDocuments(currentUserContactId, currentUserName, brokerFilePickerProps.entityId, _rootPath.current, files)
							.then(() => {
								_loadTopLevelFolderOptions(() => {
									_setNewFolder(selectedOption!)
								})
							})
							.catch(err => {
								setError(getErrorText(err))
							})
							.finally(() => {
								setUploading(false)
							})
					}}
					previewText="Drag and drop files here...">
					{uploading && <ProgressIndicator label="Uploading..." />}
					{options?.length > 0 && (
						<>
							<div style={{ position: "relative", height: Math.min(destListHeight, minHeight || 300), border: showBorder ? "1px solid rgba(198, 198, 198, .6)" : "none" }}>
								<ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto} styles={{ root: { selectors: { "& .ms-DetailsHeader": { paddingTop: 0 } } } }}>
									{/*<List onRenderCell={_onRenderDestinationListCell} items={[...options]} />*/}
									<DetailsList items={[...options]} selectionMode={SelectionMode.none} columns={_getColumns()} />
								</ScrollablePane>
							</div>
							{selectedDocs.length > 0 && (
								<div>
									<HoverCard
										cardOpenDelay={500}
										type={HoverCardType.plain}
										plainCardProps={{
											onRenderPlainCard: _onRenderPlainCard
										}}>
										<ActionButton
											iconProps={{ iconName: "Clear" }}
											onClick={() => {
												setSelectedDocs([])
												onSelectionMade([])
											}}>{`${selectedDocs.length} Selected`}</ActionButton>
									</HoverCard>
								</div>
							)}
						</>
					)}
					{!(options?.length > 0) && (
						<>
							<div className={classNames.itemCellNoHover} data-is-focusable={false}>
								<div className={classNames.itemContent}>
									<div className={classNames.itemName}>
										<span>This folder doesn't have any subfolders or files.</span>
									</div>
								</div>
							</div>
						</>
					)}
					{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
				</DragDropFile>
			)}
		</>
	)
}

function documentsPromiseChanged(prevProps: any, nextProps: any) {
	return true
}

export const FolderFilePicker = memo(FolderFilePickerInternal, documentsPromiseChanged)
